/** What Help says. Each answer was written from what the screens and the
 * server actually do -- the button a person presses, what the server refuses
 * and why -- so when a screen changes, the answer here changes with it. */

export interface FlowStep {
  title: string;
  body: string;
  /** The screen it happens on. */
  href: string;
}

export const JOB_FLOW: readonly FlowStep[] = [
  { title: "Upload", body: "Drop the City's QCP PDFs; commit the rows that read correctly.", href: "/ingest" },
  { title: "Sites", body: "Assign sites to field operators; clear the exceptions.", href: "/sites" },
  { title: "Routing", body: "Plan the days, give each one its field operators, accept.", href: "/routing" },
  { title: "Inspection", body: "The field operators fill in the checklist and submit it.", href: "/inspections" },
  { title: "Commercial", body: "Read the supplier's quote, approve, order, bill, invoice.", href: "/commercial" },
  { title: "Tracker", body: "The client's workbook, kept up to date for you.", href: "/tracker" },
];

export interface Question {
  id: string;
  question: string;
  answer: string;
  /** Words someone might search for that the text does not use. */
  keywords?: string;
}

export const QUESTIONS: readonly Question[] = [
  {
    id: "assign",
    question: "How do I assign several sites to a field operator?",
    answer:
      "On Sites, tick the sites you want. A bar appears: choose the field operator under “Assign to…” and press Assign. " +
      "Only the ticked sites change, never everything the filter shows. If a ticked site has no QCP, an " +
      "unreadable one or no position, nothing is assigned: the bar names those sites, your ticks stay, and " +
      "once they are fixed (Exceptions lists them) you press Assign again.",
    keywords: "assign sites crew field operator provider bulk select many",
  },
  {
    id: "qcp",
    question: "A QCP didn't read. What now?",
    answer:
      "In the upload's review a document that could not be read shows as “Unreadable” and is skipped when you " +
      "commit. It is usually a scan with no text in it: ask the City for the original PDF and upload that. A " +
      "document the reader was unsure of (under 80% confidence) waits in the Extraction queue, least sure " +
      "first, to be checked against the PDF. Problems left on committed sites are listed on Exceptions.",
    keywords: "upload ingest pdf scan unreadable extraction confidence",
  },
  {
    id: "quotation",
    question: "Why can't I save a quotation I read in?",
    answer:
      "Save stays grey while anything under “blocking” is open, and the list says what: a line with no " +
      "price-list code (choose the part, or “Not a catalogue part (keep as quoted)”), a supplier who is not " +
      "on Providers (add them there), totals that do not add up (the PDF was misread), or no inspection " +
      "chosen. The inspection must be submitted first, and a supplier quotes an inspection only once: " +
      "amend the quotation that exists instead.",
    keywords: "quote reader supplier price a line price list unmatched blocking",
  },
  {
    id: "order",
    question: "Why can't I issue a purchase order?",
    answer:
      "A supplier order is raised when its quotation is approved, so approve the quotation first. Then open " +
      "the job on Commercial, price any line that shows no price, and press “Issue to the supplier”. Once " +
      "issued its prices are locked, and a cancelled order cannot be issued. If the quoted prices had lapsed " +
      "by the time it was approved, no order is raised until someone confirms the prices still hold.",
    keywords: "po purchase order supplier issue approve price a line",
  },
  {
    id: "signal",
    question: "The field operators have no signal. Is their work lost?",
    answer:
      "No. Site status changes and prep-pack ticks are kept on the phone, and the badge on My work says how " +
      "many are waiting. They send by themselves when the signal comes back, or at once when the field operator taps " +
      "the badge. A prep pack opened earlier still opens without signal. Photos, “Couldn't do it today” and " +
      "the inspection checklist need a signal; the checklist says when a save did not go through.",
    keywords: "offline signal network crew field operator phone tablet queue waiting",
  },
  {
    id: "invoice",
    question: "Can I change an invoice after it is issued?",
    answer:
      "No. An issued tax invoice is fixed: its amount and VAT rate are stamped when it is issued, and each " +
      "BOQ has one invoice. If the BOQ changes afterwards, the invoice warns that the two now differ. A " +
      "wrong invoice is credited and reissued, outside the app.",
    keywords: "invoice credit note reissue vat boq edit",
  },
];

/** True when every word typed appears in the question, its answer or its
 * keywords. Several words narrow, as a search box is expected to. */
export function matchesQuestion(question: Question, query: string): boolean {
  const words = query
    .toLowerCase()
    .replace(/[“”"']/g, " ")
    .split(/\s+/)
    .filter((word) => word !== "");
  if (words.length === 0) {
    return true;
  }
  const text = `${question.question} ${question.answer} ${question.keywords ?? ""}`.toLowerCase();
  return words.every((word) => text.includes(word));
}
